import { BaseTab, getRecipeColor, IconButton, Stack, Typography, useTabState } from '@ui-components/neo.tax';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useRdContext } from '@neo.tax/rd/context';
import { useRdPageLockStatus, useRdProject } from '@neo.tax/rd/hooks';
import {
  getPath,
  NavLinkTo,
  NavigateTo,
  Path,
  HIGHLIGHT_VARIANT,
  SideDrawerSkeleton,
  useCurrentCompanyId,
  useHasSkippedConnectingTicketing,
  useCompanyMembers,
} from '@neo.tax/shared';

import { RdProjectDetailPane } from '../rd-project-detail-pane';
import { ProjectEligibility } from '../rd-project-eligibility';
import { RdProjectTeamMembers } from '../rd-project-team-members';
import { useRdProjectDetailTabIds } from './hooks';
import {
  StyledBaseTabPanel,
  StyledBaseTabs,
  StyledCloseIcon,
  StyledRdTicketsPane,
  StyledStack,
  StyledTabPanelContainer,
} from './rd-project-detail.styled';

interface RdProjectDetailProps {
  rdProjectId: string;
}

export const RdProjectDetail = ({ rdProjectId }: RdProjectDetailProps): React.ReactElement => {
  const companyId = useCurrentCompanyId();
  // The update-details lock is opt-in; while a run is in progress the project must not be editable here.
  const { pageLockStatus } = useRdPageLockStatus({ disableIfProjectDetailsUpdateInProgress: true });
  const { fiscalYearId } = useRdContext();
  const { hasSkippedConnectingTicketing } = useHasSkippedConnectingTicketing();

  const navigate = useNavigate();
  const location = useLocation();

  const { detailTabId, eligibilityTabId, tabHashMapping, teamMembersTabId, ticketsTabId } = useRdProjectDetailTabIds();

  // Get tab ID from hash
  const getTabIdFromHash = useCallback(
    (hash: string) => {
      const foundEntry = Object.entries(tabHashMapping).find(([_, itemHash]) => hash === itemHash);
      const [tabId] = foundEntry || [detailTabId];
      return tabId;
    },
    [tabHashMapping, detailTabId],
  );

  // Get hash from tab ID
  const getHashFromTab = useCallback(
    (currentTabId: string) => {
      const hash = tabHashMapping[currentTabId];
      return hash ? hash : '';
    },
    [tabHashMapping],
  );

  // Initialize initialTabId based on hash
  const initialTabId = useMemo(() => {
    const currentHash = location.hash.replace('#', '');
    const tabId = getTabIdFromHash(currentHash);
    return tabId;
  }, [location.hash, getTabIdFromHash]);

  // On change tab, update the url hash and tab state
  const onChangeTab = useCallback(
    (tabId: string | null | undefined) => {
      const hash = getHashFromTab(tabId ?? '');
      const currentPath = location.pathname;
      const currentSearch = location.search;
      navigate(`${currentPath}${currentSearch}` + (hash ? `#${hash}` : ''), { replace: true });
    },
    [location.pathname, location.search, navigate, getHashFromTab],
  );

  // Initialize tab state based on initialTabId
  const tabState = useTabState({
    defaultSelectedId: initialTabId,
    setSelectedId: onChangeTab,
  });

  // Handle browser back/forward navigation, update the tab state if the url hash is different from the tab state
  useEffect(() => {
    const currentHash = location.hash.replace('#', '');
    const expectedTabId = getTabIdFromHash(currentHash);

    // Only update if the tab state doesn't match the URL (browser navigation)
    if (tabState.selectedId !== expectedTabId) {
      tabState.setSelectedId(expectedTabId);
    }
  }, [location.hash, tabState, getTabIdFromHash]);

  const { loading, rdProject } = useRdProject(rdProjectId);
  const members = useCompanyMembers(companyId);

  // Our page lock only takes the focus away from children of the same component tree.
  // Since project drawer is a different tree of component, we cannot automatically lock this component without special treatment.
  // We have a plan to replace project drawer with a component that opens full screen, which will allow us to lock this component.
  if (!loading && (!rdProject || pageLockStatus.locked)) {
    return <NavigateTo to={getPath({ path: Path.RD_PROJECTS, companyId, fiscalYearId })} />;
  }

  // Show loading state while fetching project data
  if (loading || !rdProject) {
    return <SideDrawerSkeleton />;
  }

  return (
    <StyledStack direction="column" overflow="hidden" fullHeight>
      <Stack
        direction="row"
        alignItems="start"
        justifyContent="space-between"
        paddingX={5}
        paddingY={5}
        backgroundColor={getRecipeColor(HIGHLIGHT_VARIANT, 'colorBg').bg}
      >
        <Stack direction="column" gap={1}>
          <Typography
            color={getRecipeColor(HIGHLIGHT_VARIANT, 'colorBg').highlightText}
            fontWeight="medium"
            fontSize="lg"
          >
            {rdProject.name}
          </Typography>
        </Stack>
        <NavLinkTo to={getPath({ path: Path.RD_PROJECTS, companyId, fiscalYearId })}>
          <IconButton
            active
            variant="lightGray"
            icon={{
              node: <StyledCloseIcon />,
              isNotificationToggled: false,
            }}
            size="sm"
          />
        </NavLinkTo>
      </Stack>
      <StyledBaseTabs state={tabState} color={getRecipeColor(HIGHLIGHT_VARIANT, 'whiteBg').text}>
        <BaseTab label="Details" tabId={detailTabId} />
        <BaseTab label="R&D Eligibility" tabId={eligibilityTabId} />
        <BaseTab label="Team" tabId={teamMembersTabId} />
        {hasSkippedConnectingTicketing ? <></> : <BaseTab label="Tickets" tabId={ticketsTabId} />}
      </StyledBaseTabs>
      <StyledTabPanelContainer>
        <StyledBaseTabPanel state={tabState} tabId={detailTabId}>
          <RdProjectDetailPane members={members} rdProject={rdProject} />
        </StyledBaseTabPanel>
        <StyledBaseTabPanel state={tabState} tabId={eligibilityTabId}>
          <ProjectEligibility rdProject={rdProject} />
        </StyledBaseTabPanel>
        <StyledBaseTabPanel state={tabState} tabId={teamMembersTabId}>
          <RdProjectTeamMembers rdProject={rdProject} />
        </StyledBaseTabPanel>
        <StyledBaseTabPanel state={tabState} tabId={ticketsTabId}>
          <StyledRdTicketsPane rdProject={rdProject} />
        </StyledBaseTabPanel>
      </StyledTabPanelContainer>
    </StyledStack>
  );
};
